import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "../apiAdmin";
import { rememberFaceIdHere } from "../lib/adminFaceId";
import { isNotFound, withQuietRetry } from "../utils/adminRetry";
import AdminLoadFailed from "./AdminLoadFailed";

// Face ID for the signed-in admin (Admins tab): which devices have it and the
// switch that turns it off everywhere. Turning it ON happens on the sign-in
// screen, right after a password + code sign-in — the only door that may add
// one (server lib/admin/passkeys.js). A server without Face ID answers 404;
// then the card stays hidden. Any other failed read says so, with Retry.

type FaceIdDevice = { label: string; createdAt: string | null; lastUsedAt: string | null; synced: boolean };

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "never";

export default function AdminFaceIdCard() {
  const [devices, setDevices] = useState<FaceIdDevice[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const live = useRef(true);

  const load = useCallback(() => {
    setFailed(false);
    withQuietRetry(() => adminApi.get("/passkeys")).then(
      (r) => {
        if (live.current) setDevices(Array.isArray(r.data?.passkeys) ? r.data.passkeys : []);
      },
      (err) => {
        if (live.current) setFailed(!isNotFound(err));
      }
    );
  }, []);

  useEffect(() => {
    live.current = true;
    load();
    return () => {
      live.current = false;
    };
  }, [load]);

  const turnOff = async () => {
    if (!window.confirm("Turn off Face ID on all your devices? Your password and code still work.")) return;
    setBusy(true);
    try {
      await adminApi.delete("/passkeys");
      rememberFaceIdHere(false);
      setDevices([]);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Couldn't turn Face ID off. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (failed) {
    return (
      <div className="adm-card adm-mb-20" id="adm-faceid-card">
        <h2 className="adm-section-title adm-mb-14">Face ID sign-in</h2>
        <AdminLoadFailed inline what="your Face ID devices" onRetry={load} />
      </div>
    );
  }
  if (devices === null) return null;
  return (
    <div className="adm-card adm-mb-20" id="adm-faceid-card">
      <h2 className="adm-section-title adm-mb-14">Face ID sign-in</h2>
      {devices.length === 0 ? (
        <p className="muted-text">Off. You'll be offered it after your next password sign-in.</p>
      ) : (
        <>
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px" }}>
            {devices.map((d, i) => (
              <li key={i} style={{ padding: "6px 0" }}>
                <span className="adm-semibold">{d.label || "A device"}</span>
                <span className="muted-text">
                  {" "}
                  · added {day(d.createdAt)} · last used {day(d.lastUsedAt)}
                  {d.synced ? " · synced with iCloud Keychain" : ""}
                </span>
              </li>
            ))}
          </ul>
          <button className="adm-btn-sm adm-btn-red-sm" onClick={turnOff} disabled={busy}>
            {busy ? "Turning off…" : "Turn off Face ID"}
          </button>
        </>
      )}
    </div>
  );
}
